/**
 * Console SPA entry.
 *
 * Bun's HTML bundler compiles this at serve time from
 * `src/server/index.ts`'s `"/"` route — there is no separate build step, the
 * same arrangement `backend/`'s own dev server uses.
 *
 * The global reset lives here rather than in a stylesheet because it is four
 * rules and the design ships everything else as inline style. Both colours
 * it names come from `tokens.ts`; no hex literal appears in this file.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { LED_AI_MARK_URL } from "./assets/mark";
import { AuthGate, loadPublishableKey } from "./auth/AuthGate";
import { CompanyEntry } from "./auth/CompanyEntry";
import { Shell } from "./shell/Shell";
import { ADR, C, DS, FONT_MONO, FONT_TEXT, H, RD, RT } from "./tokens";

// The browser tab uses the compact version of the same hard-hat-and-glasses
// mark shown in the workspace lockup. Keeping it in the client bundle avoids
// a second, easily stale favicon asset and works in every served environment.
const favicon = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
  ?? document.head.appendChild(document.createElement("link"));
favicon.rel = "icon";
favicon.type = "image/png";
favicon.href = LED_AI_MARK_URL;

const reset = document.createElement("style");
reset.textContent = `
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  html, body, #root { height: 100%; }
  body {
    background: ${C.canvas};
    color: ${C.ink};
    font-family: ${FONT_TEXT};
    font-size: 18.4px;
    -webkit-font-smoothing: antialiased;
  }
  /*
   * A button's label is not prose.
   *
   * user-select: none, because clicking one — a property's value to open its
   * picker, a chip, a row's action — left the label highlighted in the
   * browser's selection blue, and a double-click selected the word. The
   * property table made this obvious: the value IS the control there, so
   * every pick flashed a blue box over the thing you had just chosen.
   * Nothing in a button is text somebody reads and copies, and the values
   * that ARE have their own copy affordance.
   *
   * Scoped to buttons on purpose. Prose, values and the description stay
   * selectable, because selecting those is a real thing to want.
   */
  button {
    font: inherit;
    color: inherit;
    background: none;
    border: 0;
    cursor: pointer;
    user-select: none;
    -webkit-user-select: none;
  }
  a { color: inherit; text-decoration: none; }
  /*
   * THE ICON RAIL's fly-out labels (the design's [data-nav="rail"] .nav::after).
   *
   * A rail row has no room for its label, so the label becomes a tooltip — and
   * it has to escape the sidebar, which is overflow: hidden. position:
   * fixed is what escapes it; the row's own --tip-y (set on hover, from the
   * row's measured centre) is what puts it beside the icon it belongs to.
   * title would do none of this: it is slow, it is styled by the OS, and on
   * a 64px rail it is the only thing standing between an icon and a guess.
   */
  .railrow { position: relative; }
  .railrow::after {
    content: attr(data-tip);
    position: fixed;
    left: 78px;
    top: var(--tip-y, -999px);
    transform: translateY(-50%);
    background: ${C.ink};
    color: ${C.surface};
    font-family: ${FONT_TEXT};
    font-size: 14.37px;
    font-weight: 600;
    white-space: nowrap;
    padding: 6px 10px;
    border-radius: 8px;
    box-shadow: 0 6px 18px ${C.scrim};
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.12s ease;
    z-index: 40;
  }
  .railrow:hover::after, .railrow:focus-visible::after { opacity: 1; }

  /*
   * THE SIDEBAR'S COLLAPSE INTO THE RAIL — structure here, motion below.
   *
   * These rules are what folds 268px of workspace down to a 64px column of
   * icons, so they apply whether or not motion is welcome. Only the
   * TRANSITIONS live in the motion block, which leaves
   * \`prefers-reduced-motion\` with exactly the instant change the console
   * had before any of this.
   *
   * The whole thing turns on one decision: the labels are never unmounted.
   * They used to be (\`{!rail && <span>}\` at a dozen sites in Shell.tsx),
   * which is why hiding the sidebar could only ever snap — by the time the
   * width changed there was no text left on screen to animate. Every one of
   * them is now a \`.navlabel\`: a flex child with \`min-width: 0\`, so the
   * sidebar's own width squashes it to nothing on the way down and lets it
   * back out on the way up. Nothing animates a label's width directly; it
   * simply answers to the box it is in, which is why the retract stays in
   * lockstep with the wall no matter how the timing is tuned.
   */
  .navlabel { min-width: 0; overflow: hidden; white-space: nowrap; }
  [data-nav="rail"] .navlabel { opacity: 0; transform: translateX(-6px); pointer-events: none; }
  /* A section heading ("Views", "Projects") has no icon to retract into, so
     it folds vertically instead — arriving at the design's 12px spacer
     rather than swapping to it. */
  /* Reserve the loaded font's line box while the fallback face is visible.
     A font swap must not shift the record links under an open popover. */
  .navgrp .navlabel { display: block; line-height: 14px; max-height: 14px; }
  [data-nav="rail"] .navgrp .navlabel { max-height: 0; }
  /* "Show all (N)" is text with no rail identity at all: it folds away
     completely rather than leaving a blank row in the icon column.
     !important for the same reason the container query above needs it —
     this codebase styles inline, and padding set on the element would
     otherwise win. */
  /* \`--fold-h\` is the row's OWN open height, set per element. A single
     shared maximum would have to clear the tallest of them, and every
     shorter row would then spend the first stretch of the fold animating
     height it does not have — visible as a pause before anything moves. */
  .navfold { overflow: hidden; max-height: var(--fold-h, 32px); }
  [data-nav="rail"] .navfold {
    max-height: 0 !important;
    opacity: 0;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  /*
   * A CAROUSEL PANEL is its own container, and the rows inside it answer to
   * ITS width rather than the viewport's (the design's
   * @container (max-width:400px)).
   *
   * Two-up on a wide desktop a panel is ~436px and a title fits on the line
   * with its pill; one-up on a phone — or two-up in a narrow window — it is
   * under 400px, and there the title takes its own line and wraps instead of
   * showing four words and an ellipsis. The viewport cannot answer that
   * question: the same 390px panel happens at 390px and at 1000px.
   *
   * !important is load-bearing: the rows carry inline styles (this codebase
   * styles components inline), and a container query in a stylesheet loses to
   * an inline declaration without it.
   */
  @container (max-width: 400px) {
    .brief-item-row { flex-wrap: wrap !important; row-gap: 6px !important; align-items: flex-start !important; }
    .brief-item-row .brief-item-title {
      order: 5 !important;
      flex: 1 0 100% !important;
      white-space: normal !important;
      overflow: visible !important;
      line-height: 1.35 !important;
    }
    .brief-item-row .brief-item-right { margin-left: auto !important; }
  }

  /* The design's scrollbar treatment on its scrolling panes (class "sb"). */
  .sb::-webkit-scrollbar { width: 9px; }
  .sb::-webkit-scrollbar-thumb { background: ${C.line}; border-radius: 5px; }
  .sb::-webkit-scrollbar-track { background: transparent; }

  /*
   * THE SIGN-IN SCREEN'S ONE BORROWED SURFACE.
   *
   * The form on the sign-in page is Clerk's \`<SignIn>\`, themed through its
   * \`appearance\` API (see \`auth/SignInScreen.tsx\`). Two things that API
   * cannot reach are written here instead.
   *
   * The "Secured by Clerk" row is the first: it is a sibling of
   * \`.cl-footerAction\` inside \`.cl-footer\` with no stable element key of its
   * own, carrying only a hashed \`cl-internal-*\` class that changes between
   * Clerk releases. Selecting it as "the footer child that is not the footer
   * action" survives that churn. Scoped to the sign-in screen so no other
   * Clerk surface — the \`<UserButton>\` popover in the sidebar — is touched.
   *
   * If Clerk's plan for this instance allows branding to be switched off in
   * their dashboard, do that and delete this rule: a toggle cannot be broken
   * by a class rename.
   */
  [data-auth-frame] .cl-footer > :not(.cl-footerAction) { display: none; }
  /* The gap the hidden row leaves behind. */
  [data-auth-frame] .cl-footer { row-gap: 0; }

  /*
   * "LAST USED", the badge Clerk puts on the provider this browser signed in
   * with last. Two problems, both visible only to a RETURNING visitor, which
   * is why neither showed up in a fresh headless run:
   *
   *  1. It was cut in half. It hangs off the button's top-right corner, past
   *     the button's own edge, and the form column used to scroll — a box
   *     that scrolls on one axis clips on the other. The column no longer
   *     scrolls (\`SignInFrame\`) and these ancestors are pinned visible, so
   *     there is nothing left to cut it.
   *  2. It was a pale chip with a hairline, sitting ON a white control,
   *     reading as a broken second border. Ink, like the CTA below it.
   *
   * Written here as well as in the appearance object because an appearance
   * key can LOSE to Clerk's own CSS — the field's \`height\` did, computing
   * back to 36px. Two classes of specificity beat their one, whatever the
   * injection order.
   */
  [data-auth-frame] .cl-socialButtonsRoot,
  [data-auth-frame] .cl-socialButtons,
  [data-auth-frame] .cl-card,
  [data-auth-frame] .cl-cardBox { overflow: visible; }
  /*
   * "USE PASSKEY INSTEAD" — the row under Continue (design 1a).
   *
   * Clerk renders it as a card ACTION carrying the \`usePasskey\` id, so it
   * inherits \`footerActionLink\`'s gold and the footer's left alignment: it
   * arrived looking like a link out of the page, hard against the CTA. The
   * design has it centred, 18px down, in quiet ink — an alternative route to
   * the same place.
   *
   * Here as well as in the appearance object for the reason the badge is:
   * an appearance key can lose to Clerk's own CSS, and the LINK inside this
   * row has no id of its own to address from there.
   */
  [data-auth-frame] .cl-footerAction__usePasskey {
    margin-top: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  [data-auth-frame] .cl-footerAction__usePasskey .cl-footerActionLink,
  [data-auth-frame] .cl-footerAction__usePasskey button {
    font-size: 13.5px;
    font-weight: 700;
    letter-spacing: -0.1px;
    color: ${C.signInQuiet};
  }
  [data-auth-frame] .cl-footerAction__usePasskey .cl-footerActionLink:hover,
  [data-auth-frame] .cl-footerAction__usePasskey button:hover { color: ${C.ink}; }

  /* SIGN-UP's footer row and terms line are ours (see \`SignUpScreen.tsx\`),
     so Clerk's own footer would render a second, differently-worded
     "Already have an account?" underneath them. */
  [data-auth-frame="sign-up"] .cl-footer { display: none; }
  /* Clerk's card compensates a border we removed — see \`authFrame.tsx\`.
     Left in place it puts every control 1px past the column's left edge. */
  [data-auth-frame] .cl-card { margin: 0; }
  [data-auth-frame] .cl-socialButtonsBlockButton { position: relative; }
  [data-auth-frame] .cl-lastAuthenticationStrategyBadge {
    background: ${C.ink};
    color: ${C.signInOn};
    border: none;
    /* The PLACEMENT is pinned here too, not just the colour: with the badge
       left static, a \`right\` of any value does nothing and it would drop
       into the button's flow beside the provider's name. */
    position: absolute;
    top: 0;
    transform: translateY(-50%);
    right: 12px;
    max-width: none;
    overflow: visible;
    text-overflow: clip;
    white-space: nowrap;
    /* The console's eyebrow, at badge size — the same mono small-caps the
       field labels and the divider use, so the one Clerk-supplied label on
       the page is set in the page's own voice. */
    font-family: ${FONT_MONO};
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.2px;
    line-height: 1.5;
    text-transform: uppercase;
    padding: 3px 8px;
    border-radius: 999px;
    box-shadow: none;
  }

  /*
   * THE ACCOUNT POPOVER'S TWO UNREACHABLE PIECES — design 3a.
   *
   * The menu itself is Clerk's \`<UserButton>\`, themed through its
   * \`appearance\` API in \`auth/userMenu.ts\`. Two things that API cannot
   * express are written here, for the same reason the sign-in screen's
   * borrowed surface is.
   *
   * The first is the ring the trigger wears WHILE the menu is open. 3a draws
   * the sidebar avatar with a 3px gap in the rail's own ground and a 2px ink
   * ring outside it, so the control reads as held down rather than merely
   * hovered — and without it the open card floats off nothing. Clerk exposes
   * that state only as \`aria-expanded\` on the trigger; there is no
   * appearance key for it. The gap is \`railGround\` because the footer this
   * sits in is the rail, and a ring gap in any other tone draws a visible
   * disc around the avatar.
   *
   * The second is "Secured by Clerk". \`userButtonPopoverFooter\` is set to
   * \`display: none\` in the appearance object and that is the supported way
   * to say it; this rule is the one that survives an appearance key losing to
   * Clerk's own CSS, which has happened here before. Unlike the sign-in
   * screen's version it needs no \`[data-auth-frame]\` scope — the popover is
   * the only Clerk surface a SIGNED-IN console renders.
   *
   * If Clerk's plan for this instance allows branding to be switched off in
   * their dashboard, do that and delete both: a toggle cannot be broken by a
   * class rename.
   */
  .cl-userButtonTrigger[aria-expanded="true"] {
    box-shadow: 0 0 0 3px ${C.railGround}, 0 0 0 5px ${C.ink};
  }
  .cl-userButtonPopoverFooter { display: none; }
  /* The custom Integrations row: same live row as Manage account. Backstop
     for userMenu.ts, in case a Clerk default outranks the appearance key
     (the sign-in field's height once did). */
  .cl-userButtonPopoverCustomItemButton {
    background: transparent !important;
    color: ${C.ink} !important;
    font-size: 14px !important;
    font-weight: 600 !important;
    opacity: 1 !important;
  }
  .cl-userButtonPopoverCustomItemButton:hover,
  .cl-userButtonPopoverCustomItemButton:focus-visible { background: ${C.sunk} !important; }
  .cl-userButtonPopoverCustomItemButton svg { color: ${C.ink}; opacity: 1; }

  /*
   * MOTION. Every animated rule in the console lives inside this one
   * media query, so \`prefers-reduced-motion\` removes the MOTION and
   * nothing else: the skeletons still reserve their space, the Ask
   * composer still opens, the suggestions are still there. The query is
   * written as \`no-preference\` rather than as a \`reduce\` override so a
   * future animation cannot be added outside it by accident — there is no
   * animation to override, because none is declared until motion is
   * explicitly welcome.
   */
  @keyframes ledai-sheen { from { background-position: 180% 0; } to { background-position: -180% 0; } }
  @keyframes ledai-rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
  @keyframes ledai-pop { from { opacity: 0; transform: translateY(10px) scale(0.97); } to { opacity: 1; transform: none; } }
  /*
   * ASK LED's turn, both ends of it — the phone's cadence, in three rules.
   * \`AskPage.tsx\` animates the sent bubble, the wait, and the reveal; the
   * console used to animate none of them, so a question and its answer both
   * simply appeared and the pause between them looked like a stall.
   *
   * \`ledai-sent\` is sharper and shorter than \`ledai-arrive\` on purpose:
   * sending is the reader's own action and should feel immediate, while the
   * answer is someone else's and reads better easing in.
   */
  @keyframes ledai-sent { from { opacity: 0; transform: translateY(6px) scale(0.98); } to { opacity: 1; transform: none; } }
  @keyframes ledai-arrive { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
  @keyframes ledai-think { 0%, 100% { opacity: 0.25; transform: translateY(0); } 50% { opacity: 1; transform: translateY(-2px); } }
  /*
   * The sidebar's own three. \`ledai-nav-toggle\` replays because Shell keys
   * the button on the nav state, so it REMOUNTS across the collapse — which
   * is what carries the control across the one move CSS cannot interpolate
   * (the workspace row's row-to-column flip) as a deliberate hop rather
   * than a jump. The drawer's pair are the phone's half of the same idea.
   */
  @keyframes ledai-nav-toggle { from { opacity: 0; transform: scale(0.88); } to { opacity: 1; transform: none; } }
  @keyframes ledai-drawer-in { from { transform: translateX(-100%); } to { transform: none; } }
  @keyframes ledai-drawer-out { from { transform: none; } to { transform: translateX(-100%); } }
  @keyframes ledai-scrim-in { from { opacity: 0; } to { opacity: 1; } }
  @keyframes ledai-scrim-out { from { opacity: 1; } to { opacity: 0; } }
  /*
   * THE REVIEW QUEUE's two motions.
   *
   * Switching walks travels: the cards you are leaving go the way you are
   * going, and the ones you are going to arrive from the side you came from.
   * Down and up are separate keyframes rather than one with a sign, because
   * the class is what the queue picks and a class cannot carry an argument.
   *
   * A decided card leaves in the direction of its verdict — accepted right,
   * rejected left — and takes its colour with it. \`Later\` and an edit fade
   * straight down instead: they are not verdicts, and dressing them as one
   * would make "set aside" look like a judgement the reviewer did not make.
   */
  @keyframes ledai-rev-out-down { from { opacity: 1; transform: none; } to { opacity: 0; transform: translateY(-16px); } }
  @keyframes ledai-rev-out-up { from { opacity: 1; transform: none; } to { opacity: 0; transform: translateY(16px); } }
  @keyframes ledai-rev-in-down { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: none; } }
  @keyframes ledai-rev-in-up { from { opacity: 0; transform: translateY(-22px); } to { opacity: 1; transform: none; } }
  @keyframes ledai-swipe-approved {
    0% { transform: none; opacity: 1; background: ${C.surface}; box-shadow: 0 0 0 0 ${C.green}00; }
    18% { transform: translateX(-6px); opacity: 1; background: ${C.greenSoft}; box-shadow: 0 0 0 2px ${C.green}; }
    100% { transform: translateX(210px); opacity: 0; background: ${C.greenSoft}; box-shadow: 0 0 0 2px ${C.green}; }
  }
  @keyframes ledai-swipe-rejected {
    0% { transform: none; opacity: 1; background: ${C.surface}; box-shadow: 0 0 0 0 ${C.red}00; }
    18% { transform: translateX(6px); opacity: 1; background: ${C.redSoft}; box-shadow: 0 0 0 2px ${C.red}; }
    100% { transform: translateX(-210px); opacity: 0; background: ${C.redSoft}; box-shadow: 0 0 0 2px ${C.red}; }
  }
  @keyframes ledai-swipe-quiet { from { opacity: 1; transform: none; } to { opacity: 0; transform: translateY(12px); } }
  @keyframes ledai-review-dropdown-in { from { opacity: 0; transform: translateY(4px) scale(0.98); } to { opacity: 1; transform: none; } }
  /* A suggestion pill arriving over the composer, and a view arriving in the
     pane. Both are short and both travel UP, because in this console forward
     is up: a record comes out of the list toward you, and a suggestion rises
     off the composer it belongs to. */
  @keyframes ledai-sug-in { from { opacity: 0; transform: translateY(10px) scale(0.96); } to { opacity: 1; transform: none; } }
  /* Two names, one motion. The pane's entrance has to REPLAY on opening a
     record, and an animation only restarts when its name changes — so the
     shell alternates between these rather than remounting the view, which
     would refetch it. */
  @keyframes ledai-scope-menu-in {
    from { opacity: 0; transform: translateY(-4px) scale(0.985); }
    to { opacity: 1; transform: none; }
  }
  @keyframes ledai-view-in-a { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
  @keyframes ledai-view-in-b { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
  /* The sign-in panel's capture bars (design: "Led AI - Console Sign In",
     1a). Each bar carries its own delay and duration inline, so the row
     breathes out of step rather than pumping as one block. */
  @keyframes ledai-wave { from { transform: scaleY(0.3); } to { transform: scaleY(1); } }
  @keyframes ledai-review-fade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes rt-fade-in { from { opacity: 0; } to { opacity: 1; } }
  @media (prefers-reduced-motion: no-preference) {
    /* The records table's menus, tooltips and sheet (views/records/). */
    .rt-fade { animation: rt-fade-in 0.12s both; }
    /* Project details (views/ProjectDetails.tsx). */
    .pd-enter { animation: ledai-arrive .25s ease both; }
    .pd-modal { animation: ledai-pop .32s cubic-bezier(.2,.9,.2,1) both; }
    .pd-scrim { animation: ledai-scrim-in .2s ease both; }
    .pd-grow { transform-origin: left; animation: pd-grow .6s cubic-bezier(.2,.9,.2,1) both; }
    .pd-spin { animation: pd-spin .8s linear infinite; }
    .pd-drawer { animation: pd-slide-in .25s cubic-bezier(.2,.8,.2,1) both; }
    @keyframes pd-slide-in { from { transform: translateX(40px); opacity: 0; } to { transform: none; opacity: 1; } }
    /* A crew or search change eases the list in — 3px and a fade, nothing more. */
    .pd-swap { animation: pd-swap .26s cubic-bezier(.2,.8,.2,1) both; }
    @keyframes pd-swap { from { opacity: .35; transform: translateY(3px); } to { opacity: 1; transform: none; } }
    @keyframes pd-grow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
    @keyframes pd-spin { to { transform: rotate(360deg); } }
    .review-add-photos { transition: background 120ms ease, border-color 120ms ease, color 120ms ease; }
    /* The review card: a decided card folding to its row, and the grounds
       its controls take under the pointer. */
    .review-fade { animation: ledai-review-fade 180ms ease both; }
    .review-pill { transition: background 0.12s ease, border-color 0.12s ease; }
    .review-editable { transition: background 0.1s ease; }
    .review-tile .review-tile-remove { transition: opacity 0.12s ease; }
    .review-outline { transition: background 0.12s ease; }
    .review-reject { transition: background 0.12s ease, color 0.12s ease; }
    .review-attach-row { transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease; }
    /* With motion off the bars keep their heights and simply stand still —
       they are an ornament, and the first thing to go. */
    .signin-bar {
      animation-name: ledai-wave;
      animation-timing-function: ease-in-out;
      animation-iteration-count: infinite;
      animation-direction: alternate;
    }
    .skel {
      background-image: linear-gradient(
        90deg, transparent 0%, ${C.surface} 45%, ${C.surface} 55%, transparent 100%
      );
      background-size: 180% 100%;
      background-repeat: no-repeat;
      animation: ledai-sheen 1.5s linear infinite;
    }
    /* The workspace switcher's caret turns when its popover opens. The
       turned state is information and is set inline; only the tween is
       motion, so only the tween is in here. */
    .wscaret { transition: transform 160ms ease; }
    /* The photo mosaic's caption fades in on hover. Its PRESENCE is a plain
       rule above — the caption is the tile's accessible name and must be in
       the DOM either way; only the fade lives in here. */
    .phototile-cap { transition: opacity 130ms ease; }
    /* A filter chip taking or losing the ink fill. The daily log's category
       row used to reload the page on every press; now it filters in place,
       and without this the chip itself was the one part of that interaction
       that still snapped. Colour only — the pill must not move, or a row of
       them would shuffle under the pointer. */
    .chipswap { transition: background-color 140ms ease, color 140ms ease, border-color 140ms ease; }
    .rise { animation: ledai-rise 260ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
    /* LED-1151: opening a project replays a short stagger — greeting,
       widgets, walks — every time the scope changes. The NAME alternates
       (a/b) so the animation restarts without remounting anything. */
    .home-greet-a { animation: ledai-home-swap-a 360ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
    .home-greet-b { animation: ledai-home-swap-b 360ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
    .home-widgets-a { animation: ledai-home-rise-a 420ms cubic-bezier(0.2, 0.8, 0.2, 1) 60ms both; }
    .home-widgets-b { animation: ledai-home-rise-b 420ms cubic-bezier(0.2, 0.8, 0.2, 1) 60ms both; }
    .home-walks-a { animation: ledai-home-rise-a 420ms cubic-bezier(0.2, 0.8, 0.2, 1) 140ms both; }
    .home-walks-b { animation: ledai-home-rise-b 420ms cubic-bezier(0.2, 0.8, 0.2, 1) 140ms both; }
    .pop { animation: ledai-pop 200ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
    /* Ask Led's turn. The dot stagger is an inline \`animation-delay\` per
       dot rather than a JS timer, for the same reason the Ask bubble's
       suggestion stagger is: with motion off the delay rides on an animation
       that was never declared, so the dots are simply there. */
    .ask-sent { animation: ledai-sent 200ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
    .ask-arrive { animation: ledai-arrive 320ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
    .ask-dot { animation: ledai-think 1.2s ease-in-out infinite; }

    /*
     * THE SIDEBAR'S RETRACT.
     *
     * Asymmetric on purpose, because collapsing and expanding are not the
     * same gesture. Collapsing, the words go FIRST (140ms, no delay) and
     * the wall closes behind them, which reads as retracting INTO the
     * icons. Expanding, the rail widens first and the words unfold into
     * the space that has just appeared (90ms in), in three bands down the
     * list so the nav arrives as a column rather than as a block.
     *
     * The bands are \`nth-child\` rather than a per-row inline delay for the
     * same reason the Ask dots' stagger is inline: whichever one is not
     * declared here is simply absent when motion is off, and a delay that
     * outlived its transition would leave a label waiting on nothing.
     */
    [data-nav="open"], [data-nav="rail"] {
      transition: width 260ms cubic-bezier(0.2, 0.8, 0.2, 1);
    }
    [data-nav] .navrow, [data-nav] .navgrp, [data-nav] .navpill,
    [data-nav] .navfold, [data-nav] .navhead {
      transition:
        padding 260ms cubic-bezier(0.2, 0.8, 0.2, 1),
        gap 260ms cubic-bezier(0.2, 0.8, 0.2, 1),
        max-height 220ms cubic-bezier(0.2, 0.8, 0.2, 1),
        border-color 180ms ease,
        opacity 180ms ease;
    }
    [data-nav] .navlabel {
      transition:
        opacity 140ms ease,
        transform 140ms ease,
        max-height 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
    }
    [data-nav="open"] .navlabel, [data-nav="drawer"] .navlabel {
      transition-duration: 200ms;
      transition-delay: 90ms;
    }
    [data-nav="open"] .sb > :nth-child(n+4):nth-child(-n+8) .navlabel { transition-delay: 118ms; }
    [data-nav="open"] .sb > :nth-child(n+9) .navlabel { transition-delay: 146ms; }
    .navtoggle { animation: ledai-nav-toggle 220ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }

    /*
     * The phone's sidebar is a door, not a wall, so it swings rather than
     * narrows. Shell holds it mounted for the length of the close
     * (\`drawerPresent\`) precisely so there is something left to animate out.
     *
     * The exits are separate KEYFRAMES rather than the entrances run in
     * reverse, and that is load-bearing: an animation only restarts when its
     * NAME changes, so a rule that kept \`ledai-drawer-in\` and merely flipped
     * \`animation-direction\` would find the animation already finished and
     * snap the drawer to the reversed fill state instead of playing it.
     */
    [data-nav="drawer"] { animation: ledai-drawer-in 240ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
    [data-nav="drawer"][data-closing="true"] {
      animation: ledai-drawer-out 190ms cubic-bezier(0.4, 0, 1, 1) both;
    }
    .navscrim { animation: ledai-scrim-in 240ms ease both; }
    .navscrim[data-closing="true"] { animation: ledai-scrim-out 190ms ease both; }

    /*
     * THE REVIEW QUEUE.
     *
     * Switching walks: 140ms out, 260ms in. The out is shorter because the
     * reader has already decided — they clicked — and the console should not
     * make them watch the thing they are leaving. \`ReviewQueue.tsx\` holds the
     * navigation for exactly \`SLIDE_OUT_MS\` so the two halves meet; the pair
     * of constants is the one place these files have to agree.
     *
     * A decided card's swipe is 280ms and takes the refetch with it — the
     * queue does not re-read until the card is off. The collapse underneath
     * is a TRANSITION rather than an animation because its height is measured
     * in JS at the moment of the decision (see \`RecordCard\`); there is no
     * keyframe that could know how tall a card with three photos is.
     */
    .revout-down { animation: ledai-rev-out-down 140ms cubic-bezier(0.4, 0, 1, 1) both; }
    .revout-up { animation: ledai-rev-out-up 140ms cubic-bezier(0.4, 0, 1, 1) both; }
    .revin-down { animation: ledai-rev-in-down 260ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
    .revin-up { animation: ledai-rev-in-up 260ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
    .revswipe-approved { animation: ledai-swipe-approved 280ms cubic-bezier(0.3, 0, 0.7, 1) both; }
    .revswipe-rejected { animation: ledai-swipe-rejected 280ms cubic-bezier(0.3, 0, 0.7, 1) both; }
    .revswipe-deferred, .revswipe-edited { animation: ledai-swipe-quiet 240ms ease both; }
    .revcollapse { transition: height 280ms cubic-bezier(0.2, 0.8, 0.2, 1); }
  .review-photo-remove {
    position: absolute; top: 5px; right: 5px;
    width: 32px; height: 32px; padding: 0; border-radius: 50%;
    display: grid; place-items: center;
    background: ${C.ink}; color: ${C.surface};
    opacity: 0; pointer-events: none;
    transition: opacity 0.12s ease;
  }
  .review-photo-tile:hover .review-photo-remove,
  .review-photo-tile:focus-within .review-photo-remove { opacity: 1; pointer-events: auto; }
  .review-photo-remove:disabled { cursor: default; }
  .review-photo-remove:disabled svg { opacity: 0.5; }
  @media (hover: none) {
    .review-photo-remove { opacity: 1; pointer-events: auto; width: 40px; height: 40px; }
  }

  /* The item record's field rows (design: "Led AI - Item Record Card").
     Hover is the whole affordance here: the row tints, the copy mark fades
     in, and clicking anywhere on the row takes the value. Only CSS can say
     "on hover", which is why this is not inline like the rest of the row —
     and the mark stays up while it reads COPIED so the confirmation does not
     vanish the moment the pointer leaves. */
  .frow { transition: background 0.14s ease; }
  /* No rule above the FIRST field: a border on row one lands directly under
     the card's own top edge and reads as an empty row above the first field. */
  .frow:first-child { border-top: none; }
  .frow:hover { background: ${C.canvas}; }
  .frow[data-copy-state="done"]:hover { background: ${C.greenSoft}; }

  /* A single highlight spans every column, including task controls outside
     the title link. Keep completed tasks subdued until hovered or focused. */
  .record-row { border-radius: 8px; }
  .record-row[data-completed="true"] { background: ${C.inset}; }
  .record-row:hover, .record-row:focus-within { background: ${C.rowSelected}; }
  /* Copy controls appear immediately when their row is targeted. Copying is
     already a deliberate action, so a scale/fade flourish under the pointer
     only makes the tiny target feel like it is moving away. */
  .frow .cp { opacity: 0; }
  .frow:hover .cp, .frow:focus-within .cp, .frow[data-copy-state="done"] .cp { opacity: 1; }

  /* The Assign-to sheet's two lists. A ranked candidate is a CHOICE — the
     whole row is the target, so it has to answer the pointer, and the edge is
     what moves rather than the fill: a tinted ground under a row that already
     carries a confidence bar reads as a second bar. The leader keeps its
     green edge on hover (it is already the strongest row on the sheet), which
     is why the rule is scoped away from it. */
  .candrow { transition: border-color 0.14s ease, background 0.14s ease; }
  .candrow:not([data-lead="true"]):not([data-selected="true"]):hover { border-color: ${C.line2}; }
  .candrow:not([data-selected="true"]):hover { background: ${C.inset}; }
  /* A directory result is a plain list row, and tints like every other one. */
  .dirrow { transition: background 0.14s ease; }
  .dirrow:not([data-selected="true"]):hover { background: ${C.faint}; }

  /* The review card's two columns. The areas are what make the photo's top
     edge land on the field table's top edge: both sit in the second row, so
     the row above them (the title, and the media eyebrow beside it) sets one
     height for both. \`--media-col\` is the column's width, passed in from
     \`ReviewQueue\` so the number stays where the rest of the card's geometry
     lives. Under the breakpoint the card stacks, and the eyebrow travels with
     the media it labels. */
  .revcard {
    display: grid;
    grid-template-columns: minmax(0, 1fr) var(--media-col, 310px);
    grid-template-areas: "title medialabel" "fields media";
    column-gap: 26px;
    row-gap: 14px;
    align-items: start;
  }
  @media (max-width: 1180px) {
    .revcard {
      grid-template-columns: minmax(0, 1fr);
      grid-template-areas: "title" "fields" "medialabel" "media";
    }
  }

    /*
     * The Ask composer's suggestion pills, and the pane's view swap.
     *
     * The pills carry an inline \`animation-delay\` per pill so the strip
     * arrives as a sequence — the same arrangement as the Ask dots, and for
     * the same reason: with motion off the animation is never declared and
     * the delay rides on nothing, so the pills are simply there.
     *
     * \`.viewenter\` is on a wrapper keyed by WHAT IS OPEN, so it replays on
     * opening a record and not on a live refresh of the one already on
     * screen. Opening a walk or a punch item used to swap the whole pane in a
     * single frame, which read as the page blinking rather than as the record
     * coming forward.
     */
    .asksug { animation: ledai-sug-in 420ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
    /* Home stays in place when its project or time scope changes. These
       transitions make the newly selected scope feel acknowledged without
       disguising the fact that its data is still refreshing. */
    .project-tile {
      transition:
        transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
        background 180ms ease,
        color 160ms ease,
        border-color 180ms ease,
        box-shadow 200ms ease;
    }
    .project-tile:hover { transform: translateY(-2px); }
    .project-tile:active { transform: translateY(0) scale(0.985); }
    .home-scope-filter {
      transition: background 140ms ease, color 140ms ease, transform 160ms ease;
    }
    .home-scope-filter:hover { background: ${C.faint}; }
    .home-scope-filter:active { transform: scale(0.97); }
    .home-scope-menu { animation: ledai-scope-menu-in 180ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
    /* No transition on the strip itself: its presence is driven per-frame from
       the pointer's distance (useSuggestionStrip), and easing on top of that
       would make the pills lag the hand summoning them. The per-pill entrance
       above still runs once, when the strip first mounts. */
    .viewenter-a { animation: ledai-view-in-a 260ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
    .viewenter-b { animation: ledai-view-in-b 260ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
  }
  /* The review card ("Daily Report Editor", review screen). Grounds and
     hovers live here rather than inline, which would outrank them. */
  .review-pill { border: 1px solid rgba(20,17,12,.08); background: ${ADR.card}; }
  .review-pill:hover { background: ${ADR.paper}; border-color: rgba(20,17,12,.18); }
  [data-testid="review-field"][aria-invalid="true"] .review-pill { border-color: ${C.red}; background: ${C.redSoft}; }
  .review-editable { background: transparent; }
  .review-editable:hover { background: ${ADR.hover}; }
  .review-editable:focus { background: ${ADR.paper}; box-shadow: 0 0 0 2px rgba(20,17,12,.14); }
  [aria-invalid="true"] > .review-editable { background: ${C.redSoft}; }
  .review-menu-item:hover { background: ${ADR.menuOn} !important; }
  .review-attach-row { border: 1.5px dashed rgba(20,17,12,.16); background: transparent; color: ${RD.sub}; }
  .review-attach-row .review-attach-hint { color: ${RD.quiet}; }
  .review-attach-row:enabled:hover { background: ${ADR.hover}; border-color: rgba(20,17,12,.28); color: ${RD.ink}; }
  .review-attach-row:disabled { opacity: .55; cursor: default; }
  .review-pill-static, .review-pill-static:hover { cursor: default; background: transparent; border-color: transparent; }
  /* The phone's unset mark (sega.css): a mark with no empty state of its own
     is drawn dashed in the muted ink. */
  .record-property-unset { display: inline-flex; align-items: center; flex: none; }
  .record-property-unset svg { color: ${RD.sub}; stroke: ${RD.sub}; }
  .record-property-unset svg * { stroke-dasharray: 2.2 2.2; }
  .record-property-unset > span { opacity: .6; }

  .review-tile .review-tile-remove { opacity: 0; }
  .review-tile:hover .review-tile-remove, .review-tile:focus-within .review-tile-remove { opacity: 1; }
  @media (hover: none) { .review-tile .review-tile-remove { opacity: 1; } }
  .review-outline { background: ${ADR.card}; }
  .review-outline:enabled:hover { background: ${ADR.hover}; }
  .review-reject { background: ${ADR.card}; color: ${C.ink}; }
  .review-reject:enabled:hover { background: ${ADR.redSoft}; color: ${ADR.redInk}; }
  .review-add-photos {
    display: inline-flex; align-items: center; justify-content: center; gap: 7px;
    margin-top: 10px; min-height: 32px; padding: 6px 10px;
    border: 1px solid ${C.line}; border-radius: 6px;
    background: ${C.surface}; color: ${C.propValue};
    font-family: ${FONT_TEXT}; font-size: 13px; font-weight: 500; line-height: 18px;
    white-space: nowrap; cursor: pointer;
    box-shadow: 0 1px 2px ${C.softShadowLow};
  }
  .review-add-photos svg { flex: none; opacity: 0.7; }
  .review-add-photos:enabled:hover { background: ${C.railMenuOn}; border-color: ${C.line2}; color: ${C.ink}; }
  .review-add-photos:enabled:active { background: ${C.propOpen}; box-shadow: none; }
  .review-add-photos:disabled { opacity: 0.5; cursor: default; box-shadow: none; }
  .review-add-photos.kb:focus-visible { border-radius: 6px; }
  @media (pointer: coarse) {
    .review-add-photos { min-height: 44px; padding-inline: 12px; }
  }

  /* The project stat band's first cell has no divider: a rule against the
     card's own rounded edge reads as a scratch, and :first-child is the only
     way to say "every cell but the first". Outside the motion query, because
     a border is not a movement. */
  .statband > *:first-child { border-left: none !important; }

  /* Hover belongs outside the motion query — it is a colour, not a movement. */
  .asksug:hover { background: ${C.faint}; }

  /* Project details: Directory and Locations (views/ProjectDetails.tsx),
     the design's style-hover values, each the DS token that carries it. Its
     motion is in the one reduced-motion block above. */
  .pd-row:hover { background: ${DS.soft}; }
  .pd-tree-row:hover { background: ${DS.treeHover} !important; }
  .pd-branch:hover { background: ${DS.surface} !important; }
  .pd-link:hover { color: ${DS.accentInk} !important; }
  .pd-drop:hover, .pd-drop[data-dragging="true"] { background: ${DS.paper} !important; border-color: rgba(10,10,10,.4) !important; }
  .pd-press:active { transform: scale(.97); }
  .pd-signout:hover { color: ${DS.red} !important; }
  /* The withdrawn state lives outside it too: with motion off there is no
     transition to run and the strip simply is not there, which is the
     correct behaviour rather than a degraded one. */
  /* Presence is an inline style now, so the withdrawn state needs no rule of
     its own — but a reader who asked for less motion gets the strip at rest
     rather than tracking their pointer. */
  @media (prefers-reduced-motion: reduce) {
    .asksug-strip { opacity: 1 !important; transform: none !important; }
  }

  /* The column headers. A header chip at rest is the column's own label on a
     transparent ground — which is exactly what a plain label looked like, so
     the sort and the filters both went unfound. The caret says the header
     opens something; the hover ground says which header you are about to
     open. Colour, not movement, so both live outside the motion query. */
  .colhdr { background: transparent; }
  .colhdr:hover { background: ${C.faint}; }
  .colhdr[data-filtered="true"],
  .colhdr[aria-expanded="true"] { background: ${C.railMenuOn}; }
  .colhdr-caret { opacity: 0.34; }
  /* A column that also NARROWS wears a plainer caret than one that only
     orders — the difference a reader is hunting for when they ask where the
     filters are. */
  .colhdr-caret[data-filterable="true"] { opacity: 0.62; }
  .colhdr:hover .colhdr-caret,
  .colhdr[aria-expanded="true"] .colhdr-caret { opacity: 1; }
  .colopt:hover { background: ${C.faint}; }

  /* A keyboard user must be able to SEE where they are. Scoped to the
     console's own focusable chrome rather than every element, so it cannot
     fight a control that already draws its own focus treatment. */
  .kb:focus-visible { outline: 2px solid ${C.gold}; outline-offset: 2px; border-radius: 10px; }
  /* A typed assignee's avatar, name and clear control share one rounded
     shell. Draw its input-style focus halo around that whole shell. */
  .assignee-name-pill:has(.kb:focus-visible) { outline: 3px solid ${C.inputRing}; outline-offset: 0; }
  .assignee-name-pill .kb:focus-visible { outline: none; }

  /* The settled design's responsive geometry (its 820px breakpoint). The
     structural mobile changes (drawer, topbar) are React state; these are
     the pure-CSS halves: the content column's gutter, the page title, the
     stat value, and the Ask composer's box padding. */
  /* The daily log's report + record panel. The panel is a fixed 300px track
     beside the report; below the console's own breakpoint there is no room
     for both, so it goes under. */
  @media (max-width: 1100px) {
    .dlcols { grid-template-columns: minmax(0, 1fr) !important; }
  }

  /* The property list, Linear's way: a 20px glyph column that IS the label,
     then the value. The old 88px word column is gone (2026-09-20); the label
     lives on as the glyph's tooltip and a visually-hidden span. A 20px lead
     fits any container, so the narrow stacking rule went with it. */
  .property-row {
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr);
    column-gap: 10px;
    align-items: center;
    min-height: 32px;
    min-width: 0;
  }
  /* Linear grounds the value under the pointer. The control's own inline
     style leaves the resting background unset so this can win. */
  @media (hover: hover) {
    .property-row > [data-testid="property-value"]:not([aria-expanded="true"]):hover {
      background: rgba(10, 10, 10, 0.05);
    }
    /* Wrapped controls ground the icon, value and optional clear together. */
    .property-pill:hover {
      background: rgba(10, 10, 10, 0.05);
    }
  }
  /* The × that clears a date or assignee is in the DOM whenever there is a value to
     clear, but only shows itself under the pointer, while the picker is
     open, or when it holds focus — Linear's way. It keeps its 18px so the
     pill never jumps as it appears. */
  .property-clear {
    opacity: 0;
    transition: opacity 120ms ease;
  }
  .property-pill:hover .property-clear,
  .property-pill:focus-within .property-clear,
  .property-row[data-open] .property-clear {
    opacity: 1;
  }
  .initials::before { content: attr(data-initials); }
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* The photo mosaic's caption. Present in the DOM always — it is the
     tile's accessible name and a screen reader must reach it — and painted
     only when the pointer or the keyboard is on the tile. A caption burnt
     into every tile is a wall of dark bars, and the point of the mosaic is
     the pictures. Under reduced motion it simply appears, which is the
     correct behaviour rather than a degraded one. */
  .phototile-cap { opacity: 0; }
  .phototile:hover .phototile-cap,
  .phototile:focus-visible .phototile-cap { opacity: 1; }

  /* The property table's row hover — shadcn's hover:bg-muted/50, in this
     console's ink. It is what makes a table of rows read as rows: the one
     under the pointer is the one you are about to act on. Inline styles
     cannot express a hover, which is the only reason this rule is here.
     (No backticks in this block: it is inside a template literal, and one
     would end the string.) */
  .fieldrow:hover { background: ${C.rowSelected}; }

  /* shadcn's Input (base), for a typed value on a card (AssigneePicker). The
     size and radius are inline; the border and shadow live here because the
     focus ring is a change to both, and an inline border would outrank it. */
  .field-input {
    border: 1px solid ${C.line};
    box-shadow: 0 1px 2px rgba(10, 10, 10, 0.04);
    outline: none;
    transition: border-color 120ms ease, box-shadow 120ms ease;
  }
  .field-input::placeholder { color: ${C.sub2}; }
  .field-input:focus-visible { border-color: ${C.line2}; box-shadow: 0 0 0 3px ${C.inputRing}; }

  /* A sortable column header says so on hover. The arrow only appears on
     the column actually in force, so hover is what tells a reader the other
     seven can be tapped too. */
  .sorthdr:hover { color: ${C.ink}; }

  .pagecol { padding: 0 72px; }
  .pagetitle { font-size: 24px; }
  .statval { font-size: 30px; }
  .askbox { padding: 0 50px; }
  @media (max-width: 820px) {
    .pagecol { padding: 0 20px; }
    .pagetitle { font-size: 21px; }
    .statval { font-size: 26px; }
    .askbox { padding: 0 12px; }
  }

  /* Sidebar rows light up under the pointer — the near-white surface the
     SELECTED row wears, at half strength, so hover previews exactly what a
     click selects (the redesign's rail: rows are surfaces over a lit ground,
     never brand fills). */
  .navrow:hover { background: ${C.railRow}; }

  /* Rows inside a PORTALLED menu (the project picker) are not on the rail's
     warm ground — they sit on the surface token, where .navrow's
     translucent-white hover is a no-op you cannot see. They darken instead,
     the same ground the column menus use. A resting row must set no inline
     background: an inline one outranks this rule and the menu goes dead
     under the pointer. (No backticks in here — this comment lives inside a
     template literal.) */
  .menuopt:hover { background: ${C.faint}; }

  /* The sidebar project picker's rows (Led AI - Project Picker.dc.html):
     the design's paper ground under the pointer AND under the keyboard
     cursor, which the picker marks with data-active. A resting row sets no
     inline background, for the reason given above. */
  .pickrow:hover, .pickrow[data-active="true"] { background: ${DS.paper}; }
  /* Its trigger, a labeled select. Border and ring live HERE, not inline, or
     the hover rule could never win. Open, it wears the design's gold ring. */
  .noscroll { scrollbar-width: none; }
  .noscroll::-webkit-scrollbar { display: none; }
  .pickfield { border: 1px solid rgba(10,10,10,0.1); }
  .pickfield:hover { border-color: rgba(10,10,10,0.18); }
  .pickfield[aria-expanded="true"] { border-color: ${DS.gold}; box-shadow: 0 0 0 3px rgba(247,197,43,0.28); }

  /* LED-1151 shell + Home. The nav row hover is the design's 5% ink over the
     warm ground; a project row's pin shows on hover (or always, once pinned —
     that one is set inline); cards lift their edge under the pointer; and a
     press settles to .97. The Home stagger's animations live in the one
     reduced-motion block above. */
  [data-nav] .navrow:hover { background: ${H.rowHover}; }
  .navtoggle:hover, .navpill:hover { background: ${H.rowHover}; }
  .navpick:hover { border-color: ${H.edgeHover} !important; }
  .pc .pcShare { opacity: 0; }
  .pc:hover .pcShare, .pcShare:focus-visible { opacity: 1; }
  .pcShare:hover { background: ${H.rowHover} !important; }
  @media (max-height: 600px) {
    .home-hero-logo { display: none; }
    .home-hero-heading h1 { margin-top: 0 !important; font-size: 24px !important; }
    .home-hero-skeleton-greeting { margin-top: 0 !important; }
    .home-hero-date { margin-bottom: 12px !important; }
  }
  @media (max-height: 450px) and (max-width: 767px) {
    .home-hero-date { display: none; }
  }
  .hcard { border: 1px solid ${H.edge}; }
  a.hcard:hover, button.hcard:hover, .hcard.hover:hover { border-color: ${H.edgeHover}; }
  .hlink:hover { color: ${H.ink} !important; }
  .press { transition: transform 140ms ease; }
  .press:active { transform: scale(0.97); }
  @keyframes ledai-home-rise-a { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
  @keyframes ledai-home-rise-b { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
  @keyframes ledai-home-swap-a { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
  @keyframes ledai-home-swap-b { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

  /* The Ask composer's icon buttons (the settled design's .ibtn). Hover is a
     pseudo-class, so it lives here with the other global rules; both colours
     come from tokens. */
  .askc-btn:hover { background: ${C.faint}; }
  .askc-send { background: ${C.faint}; }
  .askc-send:hover { background: ${C.sunk}; color: ${C.ink}; }
  /* Walk detail — photo tile hover caption, row hover, viewer buttons.
     Classes because inline styles cannot express :hover. */
  .wtile .wcap { opacity: 0; transition: opacity 0.16s ease; }
  .wtile:hover .wcap, .wtile:focus-within .wcap { opacity: 1; }
  .wrowh:hover { background: ${C.sunk}; }
  .wvbtn { background: rgba(255,255,255,0.08); }
  .wvbtn:hover { background: rgba(255,255,255,0.16); }
  .ask-answer-markdown { overflow-wrap: anywhere; }
  .ask-answer-markdown p { margin: 0 0 0.8em; }
  .ask-answer-table { max-width: 100%; overflow-x: auto; margin: 0.8em 0; }
  .ask-answer-table table { width: 100%; border-collapse: collapse; font-size: 0.94em; }
  .ask-answer-table th, .ask-answer-table td { padding: 0.55em 0.75em; border-bottom: 1px solid ${C.line2}; text-align: left; vertical-align: top; min-width: 5ch; }
  .ask-answer-table th { font-weight: 600; background: ${C.faint}; white-space: nowrap; }
  .ask-answer-table tr:last-child td { border-bottom: 0; }
  .ask-answer-markdown h1, .ask-answer-markdown h2, .ask-answer-markdown h3,
  .ask-answer-markdown h4, .ask-answer-markdown h5, .ask-answer-markdown h6 {
    margin: 1.1em 0 0.45em; font-size: 1.08em; line-height: 1.4; font-weight: 700;
  }
  .ask-answer-markdown ul, .ask-answer-markdown ol {
    margin: 0.65em 0 0.9em; padding-left: 1.5em; list-style-position: outside;
  }
  .ask-answer-markdown ul { list-style-type: disc; }
  .ask-answer-markdown ol { list-style-type: decimal; }
  .ask-answer-markdown li + li { margin-top: 0.45em; }
  .ask-answer-markdown li > ul, .ask-answer-markdown li > ol { margin: 0.35em 0; }
  .ask-answer-markdown li > p { margin-bottom: 0.35em; }
  .ask-answer-markdown blockquote {
    border-left: 3px solid ${C.line2}; padding-left: 1em; margin: 0.8em 0; color: ${C.sub};
  }
  .ask-answer-markdown pre { overflow-x: auto; padding: 0.8em; margin: 0.8em 0; background: ${C.faint}; border-radius: 8px; }
  .ask-answer-markdown code { font-family: ${FONT_MONO}; font-size: 0.85em; }
  .ask-answer-markdown hr { border: 0; border-top: 1px solid ${C.line2}; margin: 1em 0; }
  .ask-answer-markdown a:not([data-testid="ask-cite"]) { text-decoration: underline; }
  .ask-answer-markdown > :first-child { margin-top: 0; }
  .ask-answer-markdown > :last-child { margin-bottom: 0; }
  .askc-input::placeholder { color: ${C.sub}; opacity: 1; }

  /* The records table (\`views/records/\`, "Led AI - Records Table"). Every
     state the pointer owns lives here, never inline — an inline background
     outranks :hover and the rule goes dead. */
  /* A board column's own scroll: cards never squeeze to fit, and the bar is
     a thin one that only darkens under the pointer, as Linear's does. */
  .rt-board-col > * { flex-shrink: 0; }
  .rt-proj-row:hover { background: ${RT.sheetFoot}; }
  .rt-board-col { scrollbar-width: thin; scrollbar-color: transparent transparent; }
  .rt-board-col:hover { scrollbar-color: rgba(10,10,10,0.18) transparent; }
  .rt-noscroll { scrollbar-width: none; }
  .rt-noscroll::-webkit-scrollbar { display: none; }
  .rt-hover:hover { background: ${RT.hover} !important; }
  .rt-hover-ink:hover { background: ${RT.hover} !important; color: ${DS.ink} !important; }
  .rt-th:hover { background: ${RT.hover}; }
  /* Column resize handle (LED-1338): a thin ink line on the header's edge. */
  .rt-resize::after { content: ""; position: absolute; top: 6px; bottom: 6px; left: 3px; width: 3px; border-radius: 2px; background: transparent; transition: background 0.12s ease; }
  .rt-resize:hover::after, .rt-resize[data-active]::after { background: rgba(10,10,10,0.28); }
  .rt-row:hover .rt-cell { background: ${RT.rowHover}; }
  .rt-row:focus-visible { outline: 2px solid ${DS.ink}; outline-offset: -2px; }
  .rt-lrow:hover, .rt-lrow:focus-visible { background: ${RT.strip}; }
  .rt-card { transition: border-color 0.14s ease; }
  .rt-card:hover, .rt-card:focus-visible { border-color: rgba(10,10,10,0.16) !important; }
  .rt-input::placeholder { color: ${DS.ink3}; opacity: 1; }
  .rt-link { text-decoration: underline; text-decoration-color: rgba(10,10,10,0.18); text-underline-offset: 3px; }
`;
document.head.appendChild(reset);

const container = document.getElementById("root");
if (!container) {
  throw new Error("#root is missing from index.html");
}

/**
 * Resolved BEFORE the first render, at module top level.
 *
 * Deliberately not fetched inside a component: doing so leaves a render pass
 * in which the shell is unmounted, and keystrokes in that window are silently
 * lost — it broke ⌘K, which the browser suite caught. Awaiting here delays the
 * page's load event instead, which is exactly the semantics wanted: by the
 * time the page is "loaded", the console is mounted and listening.
 */
const publishableKey = await loadPublishableKey();

createRoot(container).render(
  <StrictMode>
    <AuthGate publishableKey={publishableKey}>
      <CompanyEntry><Shell /></CompanyEntry>
    </AuthGate>
  </StrictMode>,
);
